import axios from 'axios';

import { API_BASE_URL } from '../config/constants';
import axiosInstance from '../config/axios';

// Ayarlar API servisi - tüm modüller için genel ayarlar yönetimi
const settingsApi = {
  // Kullanıcı ayarlarını getir
  getUserSettings: async (): Promise<any[]> => {
    try {
      const response = await axiosInstance.get(`/api/settings/user`);
      return response.data || [];
    } catch (error) {
      console.error('Kullanıcı ayarları alınırken hata:', error);
      return [];
    }
  },

  // Global ayarı kaydet (detaylı sonuç dön)
  saveGlobalSettingDetailed: async (
    key: string,
    value: any,
    description?: string,
    isActive: boolean = true
  ): Promise<{ ok: boolean; status?: number; data?: any; message?: string; method?: 'PUT' | 'POST' }> => {
    try {
      const payload = {
        settingKey: key,
        settingValue: typeof value === 'string' ? value : JSON.stringify(value),
        description: description || `${key} ayarı`,
        isActive
      };
      try {
        await axiosInstance.get(`/api/settings/global/${key}`);
        const response = await axiosInstance.put(`/api/settings/global/${key}`, payload);
        return { ok: response.status === 200, status: response.status, data: response.data, method: 'PUT' };
      } catch (preErr: any) {
        // Eğer GET başarısızsa POST dene, hata detayını da taşı
        try {
          const response = await axiosInstance.post(`/api/settings/global`, payload);
          return { ok: response.status === 201, status: response.status, data: response.data, method: 'POST' };
        } catch (postErr: any) {
          const status = postErr?.response?.status;
          const message = postErr?.response?.data?.message || postErr?.message || 'Bilinmeyen hata';
          return { ok: false, status, message, method: 'POST' };
        }
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || 'Bilinmeyen hata';
      return { ok: false, status, message };
    }
  },

  // Belirli bir kullanıcı ayarını getir
  getUserSettingByKey: async (key: string): Promise<any> => {
    try {
      const response = await axiosInstance.get(`/api/settings/user/${key}`);
      return response.data;
    } catch (error) {
      // 404: ayar henüz oluşturulmamış olabilir; bu normal bir durum, gürültü yapmayalım
      const status = (error as any)?.response?.status;
      if (status === 404) {
        return null;
      }
      console.error(`${key} kullanıcı ayarı alınırken hata:`, (error as any)?.message || error);
      return null;
    }
  },

  // Kullanıcı ayarını oluştur veya güncelle
  saveUserSetting: async (key: string, value: any, description?: string): Promise<boolean> => {
    try {
      const payload = {
        settingKey: key,
        settingValue: typeof value === 'string' ? value : JSON.stringify(value),
        description: description || `${key} ayarı`
      };
      // Önce ayarın var olup olmadığını kontrol et
      try {
        await axiosInstance.get(`/api/settings/user/${key}`);
        // Ayar varsa güncelle
        const response = await axiosInstance.put(`/api/settings/user/${key}`, payload);
        return response.status === 200;
      } catch (error) {
        // Ayar yoksa oluştur
        const response = await axiosInstance.post(`/api/settings/user`, payload);
        return response.status === 201;
      }
    } catch (error) {
      console.error(`${key} kullanıcı ayarı kaydedilirken hata:`, error);
      return false;
    }
  },

  // Kullanıcı ayarını sil
  deleteUserSetting: async (key: string): Promise<boolean> => {
    try {
      const response = await axiosInstance.delete(`/api/settings/user/${key}`);
      return response.status === 204;
    } catch (error) {
      console.error(`${key} kullanıcı ayarı silinirken hata:`, error);
      return false;
    }
  },

  // Belirli bir modül için kullanıcı ayarlarını getir
  getUserModuleSettings: async (moduleKey: string): Promise<any> => {
    try {
      // Token kontrolü
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      if (!token) {
        // Token yoksa sessizce null dön
        return null;
      }
      
      // Modül anahtarının zaten önek içerip içermediğini kontrol et
      const settingKey = moduleKey.startsWith('module_') ? moduleKey : `module_${moduleKey}`;
      
      const response = await axios.get(`${API_BASE_URL}/api/settings/user/${settingKey}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data && typeof response.data === 'string') {
        try {
          const parsedData = JSON.parse(response.data);
          return parsedData;
        } catch (parseError) {
          // Parse hatası durumunda string olarak dön
          return response.data;
        }
      }
      return response.data || null;
    } catch (error) {
      // Sadece 404 hatası değilse loglama yap
      if ((error as any)?.response?.status !== 404) {
        console.error(`${moduleKey} modülü kullanıcı ayarları alınırken hata:`, (error as any).message || error);
      }
      return null;
    }
  },

  // Belirli bir modül için kullanıcı ayarlarını kaydet
  saveUserModuleSettings: async (moduleKey: string, settings: any, description?: string): Promise<boolean> => {
    try {
      console.log(`${moduleKey} modülü için kullanıcı ayarları kaydediliyor:`, settings);

      // Modül anahtarının zaten önek içerip içermediğini kontrol et
      const settingKey = moduleKey.startsWith('module_') ? moduleKey : `module_${moduleKey}`;
      
      const result = await settingsApi.saveUserSetting(
        settingKey,
        settings,
        description || `${moduleKey} kullanıcı modül ayarları`
      );
      
      console.log(`${moduleKey} modülü kullanıcı ayarları kaydetme sonucu:`, result);
      return result;
    } catch (error) {
      console.error(`${moduleKey} modülü kullanıcı ayarları kaydedilirken hata:`, error);
      return false;
    }
  },

  // Tüm genel ayarları getir (admin)
  getGlobalSettings: async (includeInactive: boolean = false): Promise<any[]> => {
    try {
      const response = await axiosInstance.get(`/api/settings/global`, { params: { includeInactive } });
      return response.data || [];
    } catch (error) {
      console.error('Global ayarlar alınırken hata:', error);
      return [];
    }
  },

  // Belirli bir genel ayarı getir
  getGlobalSettingByKey: async (key: string): Promise<any> => {
    try {
      const response = await axiosInstance.get(`/api/settings/global/${key}`);
      return response.data;
    } catch (error) {
      // 404: ayar henüz oluşturulmamış olabilir; bu normal, gürültüyü azaltalım
      const status = (error as any)?.response?.status;
      if (status === 404) {
        return null;
      }
      console.error(`${key} global ayarı alınırken hata:`, (error as any)?.message || error);
      return null;
    }
  },

  // Global ayarı oluştur veya güncelle
  saveGlobalSetting: async (key: string, value: any, description?: string, isActive: boolean = true): Promise<boolean> => {
    try {
      const payload = {
        settingKey: key,
        settingValue: typeof value === 'string' ? value : JSON.stringify(value),
        description: description || `${key} ayarı`,
        isActive
      };
      // Önce ayarın var olup olmadığını kontrol et
      try {
        await axiosInstance.get(`/api/settings/global/${key}`);
        // Ayar varsa güncelle
        const response = await axiosInstance.put(`/api/settings/global/${key}`, payload);
        return response.status === 200;
      } catch (error) {
        // Ayar yoksa oluştur
        const response = await axiosInstance.post(`/api/settings/global`, payload);
        return response.status === 201;
      }
    } catch (error) {
      console.error(`${key} global ayarı kaydedilirken hata:`, error);
      return false;
    }
  },

  // Global ayarı sil
  deleteGlobalSetting: async (key: string): Promise<boolean> => {
    try {
      const response = await axiosInstance.delete(`/api/settings/global/${key}`);
      return response.status === 204;
    } catch (error) {
      console.error(`${key} global ayarı silinirken hata:`, error);
      return false;
    }
  },

  // Belirli bir modül için genel ayarları getir
  getGlobalModuleSettings: async (moduleKey: string): Promise<any> => {
    try {
      // Modül anahtarının zaten önek içerip içermediğini kontrol et
      const settingKey = moduleKey.startsWith('module_') ? moduleKey : `module_${moduleKey}`;
      
      const response = await axiosInstance.get(`/api/settings/global/${settingKey}`);
      
      if (response.data && response.data.settingValue) {
        try {
          return JSON.parse(response.data.settingValue);
        } catch (e) {
          return response.data.settingValue;
        }
      }
      return null;
    } catch (error) {
      // Sadece 401 ve 404 hataları değilse loglama yap
      if (!((error as any)?.response?.status === 401 || (error as any)?.response?.status === 404)) {
        console.error(`${moduleKey} modül genel ayarları alınırken hata: ${(error as any).message || 'Bilinmeyen hata'}`);
      }
      return null;
    }
  },

  // Belirli bir modül için genel ayarları kaydet (admin)
  saveGlobalModuleSettings: async (moduleKey: string, settings: any, description?: string): Promise<boolean> => {
    try {
      console.log(`${moduleKey} modülü için global ayarlar kaydediliyor:`, settings);
      
      // Token kontrolü
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      if (!token) {
        console.warn(`Token bulunamadı, ${moduleKey} modülü global ayarları kaydedilemiyor`);
        return false;
      }
      
      // Modül anahtarının zaten önek içerip içermediğini kontrol et
      const settingKey = moduleKey.startsWith('module_') ? moduleKey : `module_${moduleKey}`;
      
      const result = await settingsApi.saveGlobalSetting(
        settingKey,
        settings,
        description || `${moduleKey} global modül ayarları`
      );
      
      console.log(`${moduleKey} modülü global ayarları kaydetme sonucu:`, result);
      return result;
    } catch (error) {
      console.error(`${moduleKey} modülü global ayarları kaydedilirken hata:`, error);
      return false;
    }
  },

  // Oturum süresi ayarını getir
  getSessionTimeoutSetting: async (): Promise<number> => {
    try {
      // Varsayılan oturum süresi (dakika cinsinden)
      const defaultTimeout = 60;
      
      // Kullanıcı ayarından oturum süresini al
      const sessionSetting = await settingsApi.getUserSettingByKey('sessionTimeout');
      
      // Ayar varsa ve geçerli bir sayı ise kullan, yoksa varsayılan değeri döndür
      if (sessionSetting && !isNaN(parseInt(sessionSetting.value))) {
        return parseInt(sessionSetting.value);
      }
      
      return defaultTimeout;
    } catch (error) {
      console.error('Oturum süresi ayarı alınırken hata:', error);
      return 60; // Hata durumunda varsayılan 60 dakika
    }
  },

  // Oturum süresi ayarını kaydet
  saveSessionTimeoutSetting: async (timeoutMinutes: number): Promise<boolean> => {
    try {
      return await settingsApi.saveUserSetting(
        'sessionTimeout', 
        timeoutMinutes, 
        'Kullanıcı oturum zaman aşımı süresi (dakika)'
      );
    } catch (error) {
      console.error('Oturum süresi ayarı kaydedilirken hata:', error);
      return false;
    }
  },

  // Barkod ayarları için özel metodlar (geriye uyumluluk)
  getUserBarcodeSettings: async (): Promise<any> => {
    try {
      // API endpoint'leri henüz hazır olmadığı için doğrudan varsayılan ayarları döndür
      // Bu, 404 hatalarını önleyecektir
      console.log('Barkod ayarları için varsayılan değerler kullanılıyor');
      return settingsApi.getDefaultBarcodeSettings();
    } catch (error) {
      console.error('Kullanıcı barkod ayarları alınırken hata:', (error as any)?.message || error);
      return settingsApi.getDefaultBarcodeSettings();
    }
  },
  
  saveUserBarcodeSettings: async (settings: any): Promise<boolean> => {
    try {
      const result = await settingsApi.saveUserModuleSettings('barcode_settings', settings, 'Kullanıcı barkod ayarları');
      return result;
    } catch (error) {
      console.error('Kullanıcı barkod ayarları kaydedilirken hata:', (error as any)?.message || error);
      return false;
    }
  },

  // Varsayılan barkod ayarlarını döndüren yardımcı fonksiyon
  getDefaultBarcodeSettings: (): any => {
    // barcodeSettings.ts'deki defaultBarcodeSettings ile aynı yapıyı döndür
    return {
      activeType: 'EAN13',
      autoProcess: true,
      minLength: 5,
      maxLength: 30,
      allowAlphanumeric: false,
      validateChecksum: true,
      clearDelay: 100,
      debounceTime: 300,
      prefix: '',
      suffix: '',
      typeSettings: {
        'EAN13': {
          length: 13,
          validateChecksum: true
        },
        'EAN8': {
          length: 8,
          validateChecksum: true
        },
        'CODE39': {
          minLength: 3,
          maxLength: 30,
          validateChecksum: false
        },
        'CODE128': {
          minLength: 3,
          maxLength: 30,
          validateChecksum: false
        },
        'QR': {
          minLength: 3,
          maxLength: 100
        },
        'CUSTOM': {
          pattern: '.*',
          description: 'Özel barkod formatı'
        }
      }
    };
  },

  getGlobalBarcodeSettings: async (): Promise<any> => {
    try {
      // API endpoint'leri henüz hazır olmadığı için doğrudan varsayılan ayarları döndür
      // Bu, 404 hatalarını önleyecektir
      console.log('Genel barkod ayarları için varsayılan değerler kullanılıyor');
      return settingsApi.getDefaultBarcodeSettings();
    } catch (error) {
      console.error('Genel barkod ayarları alınırken hata:', (error as any)?.message || error);
      return settingsApi.getDefaultBarcodeSettings();
    }
  },

  saveGlobalBarcodeSettings: async (settings: any): Promise<boolean> => {
    try {
      const result = await settingsApi.saveGlobalModuleSettings('barcode_settings', settings, 'Genel barkod ayarları');
      return result;
    } catch (error) {
      console.error('Genel barkod ayarları kaydedilirken hata:', (error as any)?.message || error);
      return false;
    }
  },

  // SMTP Ayarları
  getSmtpSettings: async (): Promise<import('../types/settings').SmtpSettings | null> => {
    try {
      const res = await axiosInstance.get(`/api/settings/smtp`);
      return res.data || null;
    } catch (error) {
      console.error('SMTP ayarları alınırken hata:', (error as any)?.message || error);
      return null;
    }
  },

  saveSmtpSettings: async (
    settings: import('../types/settings').SmtpSettings
  ): Promise<{ ok: boolean; message?: string; status?: number }> => {
    try {
      const res = await axiosInstance.put(`/api/settings/smtp`, settings);
      const ok = res.status === 200;
      return { ok, message: res?.data?.message, status: res.status };
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || error?.message || 'SMTP ayarları kaydedilemedi';
      return { ok: false, message, status };
    }
  },

  testSmtpSettings: async (
    settings: import('../types/settings').SmtpSettings
  ): Promise<{ ok: boolean; message: string }> => {
    try {
      const res = await axiosInstance.post(`/api/settings/smtp/test`, settings);
      return { ok: true, message: res?.data?.message || 'Test başarılı' };
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'SMTP test başarısız';
      return { ok: false, message };
    }
  }
};

export default settingsApi;
